import { useEffect, useState, useMemo } from 'react';
import './styles.css';
import './vehicle-detail.css';
import { Layout6Root } from './Layout6Root';
import { VehicleGallery } from './VehicleGallery';
import { VehicleDetailHeader } from './VehicleDetailHeader';
import { VehicleDetailOverview } from './VehicleDetailOverview';
import { VehicleDetailFeatures } from './VehicleDetailFeatures';
import { VehicleDetailSpecs } from './VehicleDetailSpecs';
import { VehicleDetailLocation } from './VehicleDetailLocation';
import { VehicleDetailSidebar } from './VehicleDetailSidebar';
import { VehicleDetailRelated } from './VehicleDetailRelated';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { LayoutHeader as Header } from '@/layouts';
import { LayoutFooter as Footer } from '@/layouts';
import { extractIdFromSlug } from '@/lib/slug';
import { Button } from '@/components/ui/button';
import { secureGetItem, secureSetItem } from '@/lib/storage';
import { openWhatsApp } from '@/lib/whatsapp';
import SEOHead from '@/components/SEOHead';
import { generateVehicleStructuredData } from '@/lib/structuredData';
import { ArrowLeft, Share2 } from 'lucide-react';
import { useAppData } from '@/contexts/AppDataContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useStore } from '@/contexts/StoreContext';
import { useToast } from '@/hooks/use-toast';
import { requestUserLocation } from '@/lib/geolocation';
import { VehicleNotFound } from '@/components/VehicleNotFound';
import {
  trackVehicleView,
  trackWhatsAppClick,
  trackAddToFavorites,
} from '@/lib/analytics';

const VehicleDetail = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { config, vehicles, isLoading } = useAppData();
  const { selectedStore, availableStores } = useStore();

  const slugOrId = params.id || params['*'] || '';
  const [vehicle, setVehicle] = useState<any>(null);
  const [isFavorited, setIsFavorited] = useState(false);
  const [relatedVehicles, setRelatedVehicles] = useState<any[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const { colors } = useTheme();
  const { toast } = useToast();

  const buttonColor = colors.button;
  const primaryColor = colors.primary;
  const secondaryColor = colors.secondary;
  const titleColor = colors.title;
  const textColor = colors.text;

  const vehicleStore = useMemo(() => {
    if (!vehicle) {
      return selectedStore || config.stores[0];
    }
    const storeId = vehicle.store;
    if (!storeId) {
      return selectedStore || config.stores[0];
    }
    return config.stores?.find((s: any) => s._id === storeId) || config.stores[0];
  }, [vehicle, config.stores, selectedStore]);

  useEffect(() => {
    const loadVehicleData = async () => {
      if (!isLoading && vehicles.length > 0) {
        const vehicleId = (await extractIdFromSlug(slugOrId || '')) || slugOrId;
        const foundVehicle = vehicles.find((v) => v.id.toString() === vehicleId);

        if (foundVehicle) {
          setVehicle(foundVehicle);

          trackVehicleView({
            id: foundVehicle.id,
            brand: foundVehicle.brand,
            model: foundVehicle.model,
            version: foundVehicle.version || '',
            year: `${foundVehicle.manufactureYear}/${foundVehicle.modelYear}`,
            price: foundVehicle.price,
            mileage: foundVehicle.mileage,
          });

          try {
            const viewedStr = await secureGetItem('recentlyViewedVehicles');
            let viewedVehicles = viewedStr ? JSON.parse(viewedStr) : [];
            viewedVehicles = viewedVehicles.filter((v: any) => v.id !== foundVehicle.id);
            viewedVehicles.unshift({ id: foundVehicle.id, timestamp: Date.now() });
            viewedVehicles = viewedVehicles.slice(0, 20);
            await secureSetItem('recentlyViewedVehicles', JSON.stringify(viewedVehicles));
            window.dispatchEvent(new Event('vehicleViewed'));
          } catch (error) {
            console.error('Erro ao registrar veículo visualizado:', error);
          }

          const favorites = await secureGetItem('favorites');
          if (favorites && Array.isArray(favorites)) {
            setIsFavorited(favorites.includes(foundVehicle.id));
          }

          const unifiedAds = config?.unifiedAds !== false;
          const unifiedAdsStore = config?.unifiedAdsStore;
          const vehicleStoreId = foundVehicle.store;

          let filterByStore = false;
          if (!unifiedAds) {
            filterByStore = true;
          } else if (unifiedAdsStore !== null && unifiedAdsStore !== undefined) {
            filterByStore = vehicleStoreId !== unifiedAdsStore;
          }

          const sameModel = vehicles.filter(
            (v) =>
              v.id !== foundVehicle.id &&
              v.model === foundVehicle.model &&
              (!filterByStore || v.store === vehicleStoreId)
          );

          const sameBrand = vehicles.filter(
            (v) =>
              v.id !== foundVehicle.id &&
              v.brand === foundVehicle.brand &&
              v.model !== foundVehicle.model &&
              (!filterByStore || v.store === vehicleStoreId)
          );

          const priceMin = foundVehicle.price - 10000;
          const priceMax = foundVehicle.price + 10000;
          const others = vehicles.filter(
            (v) =>
              v.id !== foundVehicle.id &&
              v.brand !== foundVehicle.brand &&
              v.price >= priceMin &&
              v.price <= priceMax &&
              (!filterByStore || v.store === vehicleStoreId)
          );

          let related: any[] = [];
          if (sameModel.length > 0) {
            related = sameModel.slice(0, 8);
          } else if (sameBrand.length > 0) {
            related = sameBrand.slice(0, 8);
          } else {
            related = others.slice(0, 8);
          }

          setRelatedVehicles(related);
        }
        setHasLoaded(true);
      }
    };

    loadVehicleData();
  }, [slugOrId, vehicles, isLoading, config?.unifiedAds, config?.unifiedAdsStore]);

  if (isLoading || !hasLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!vehicle && hasLoaded) {
    return (
      <VehicleNotFound
        config={config}
        buttonColor={buttonColor}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
        titleColor={titleColor}
        textColor={textColor}
      />
    );
  }

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);

  const formatMileage = (mileage: number) => {
    if (mileage === 0) return 'Zero KM';
    return `${new Intl.NumberFormat('pt-BR').format(mileage)} km`;
  };

  const toggleFavorite = async () => {
    const favorites = (await secureGetItem('favorites')) || [];
    let updatedFavorites = Array.isArray(favorites) ? [...favorites] : [];

    if (updatedFavorites.includes(vehicle.id)) {
      updatedFavorites = updatedFavorites.filter((id: number) => id !== vehicle.id);
      setIsFavorited(false);
    } else {
      updatedFavorites.push(vehicle.id);
      setIsFavorited(true);
      trackAddToFavorites({
        id: vehicle.id,
        brand: vehicle.brand,
        model: vehicle.model,
        version: vehicle.version || '',
        year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
        price: vehicle.price,
        mileage: vehicle.mileage,
      });
    }

    await secureSetItem('favorites', updatedFavorites);
    window.dispatchEvent(new Event('favoritesUpdated'));
  };

  const handleWhatsApp = () => {
    const adLead = vehicleStore.ad?.lead;

    if (adLead?.type === 'message' && adLead?.ios) {
      let message = adLead?.ios;
      message = message.substr(message.indexOf('&text=') + 6);

      if (adLead.replace && Array.isArray(adLead.replace)) {
        adLead.replace.forEach((replaceItem: any) => {
          Object.keys(replaceItem).forEach((placeholder) => {
            const field = replaceItem[placeholder];
            let value = '';

            switch (field) {
              case 'vehicle':
                value = `${vehicle.brand} ${vehicle.model} ${vehicle.version} ${vehicle.modelYear}`;
                break;
              case 'price':
                value = formatPrice(vehicle.price);
                break;
              case 'brand':
                value = vehicle.brand;
                break;
              case 'model':
                value = vehicle.model;
                break;
              case 'version':
                value = vehicle.version;
                break;
              case 'year':
              case 'modelYear':
                value = vehicle.modelYear.toString();
                break;
              case 'mileage':
                value = `${vehicle.mileage.toLocaleString('pt-BR')} km`;
                break;
              case 'color':
                value = vehicle.color;
                break;
              case 'fuel':
                value = vehicle.fuel;
                break;
              case 'transmission':
                value = vehicle.transmission;
                break;
              default:
                value = vehicle[field] || '';
            }

            const regex = new RegExp(`\\{${placeholder}\\}`, 'gi');
            message = message.replace(regex, value);
          });
        });
      }

      const phoneMatch = vehicleStore.ad?.lead?.ios?.match(/phone=([^&]+)/);
      const phone = phoneMatch ? phoneMatch[1] : '';
      const url = `whatsapp://send?phone=${phone}&text=${encodeURIComponent(message)}`;

      trackWhatsAppClick('vehicle_detail', {
        id: vehicle.id,
        brand: vehicle.brand,
        model: vehicle.model,
        version: vehicle.version || '',
        year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
        price: vehicle.price,
        mileage: vehicle.mileage,
      });

      openWhatsApp(url);
    } else {
      const message = `Olá,\n\nTenho interesse no veículo abaixo e gostaria de receber mais informações.\n\n${vehicle.brand} ${vehicle.model} ${vehicle.version}, ${formatPrice(vehicle.price)}\n\nFico no aguardo,`;
      const url = `${vehicleStore.ad?.lead?.ios}&text=${encodeURIComponent(message)}`;

      trackWhatsAppClick('vehicle_detail', {
        id: vehicle.id,
        brand: vehicle.brand,
        model: vehicle.model,
        version: vehicle.version || '',
        year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
        price: vehicle.price,
        mileage: vehicle.mileage,
      });

      openWhatsApp(url);
    }
  };

  const financingService = vehicleStore?.services?.find(
    (s: any) => s.service === '65dcb7898eca108d87c91dbc'
  );

  const testDriveService = vehicleStore?.services?.find(
    (s: any) => s.service === '65dcb5e78eca108d87c91db8'
  );

  const showTestDrive = vehicleStore?.ad?.showTestDriveOption && testDriveService;

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${vehicle.brand} ${vehicle.model} ${vehicle.version}`,
          text: `Confira este ${vehicle.brand} ${vehicle.model} por ${formatPrice(vehicle.price)}`,
          url: window.location.href,
        });
      } catch {
        /* user cancelled */
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast({
        title: '✅ Link copiado',
        description: 'O link do veículo foi copiado para a área de transferência.',
      });
    }
  };

  const handleRequestLocation = async () => {
    const loc = await requestUserLocation();
    if (loc) setUserLocation(loc);
  };

  const shareUrl = `${window.location.origin}${location.pathname}${location.search}`;
  const canonicalVehicleUrl = `${window.location.origin}${location.pathname}`;
  const seoVehicleTitle = `${vehicle.brand} ${vehicle.model}${vehicle.version ? ` ${vehicle.version}` : ''} ${vehicle.modelYear}`;

  return (
    <Layout6Root withHeader className="bg-background">
      <SEOHead
        title={seoVehicleTitle}
        description={`${vehicle.brand} ${vehicle.model}${vehicle.version ? ` ${vehicle.version}` : ''} ${vehicle.modelYear} — ${vehicle.color} — ${vehicle.manufactureYear}/${vehicle.modelYear} — ${vehicle.fuel} — ${vehicle.transmission} — ${vehicle.mileage.toLocaleString('pt-BR')} km — ${formatPrice(vehicle.price)}`}
        keywords={`${vehicle.brand}, ${vehicle.model}, ${vehicle.modelYear}, ${vehicle.fuel}, ${vehicle.transmission}, ${vehicle.color}, veículo usado, carro seminovo`}
        image={vehicle.photos?.[0]}
        imageAlt={seoVehicleTitle}
        type="product"
        url={shareUrl}
        canonicalUrl={canonicalVehicleUrl}
        structuredData={generateVehicleStructuredData({
          vehicle,
          config,
          store: vehicleStore,
          vehiclePageUrl: shareUrl,
        })}
      />
      <Header config={config} />

      <div className="wl6-vd-breadcrumb">
        <div className="wl6-container wl6-vd-breadcrumb__inner">
          <nav className="wl6-vd-breadcrumb__trail" aria-label="Breadcrumb">
            <Link to="/">Início</Link>
            <span aria-hidden>/</span>
            <Link to="/estoque">Estoque</Link>
            <span aria-hidden>/</span>
            <span className="wl6-vd-breadcrumb__current">
              {vehicle.brand} {vehicle.model}
            </span>
          </nav>
          <button type="button" className="wl6-vd-back" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </button>
        </div>
      </div>

      <section className="wl6-vd-page">
        <div className="wl6-container">
          <VehicleDetailHeader
            vehicle={vehicle}
            titleColor={titleColor}
            textColor={textColor}
            formatPrice={formatPrice}
          />

          <VehicleGallery
            key={vehicle.id}
            photos={vehicle.photos}
            alt={`${vehicle.brand} ${vehicle.model}`}
            isZeroKm={vehicle.isZeroKm}
            isFavorited={isFavorited}
            onFavorite={toggleFavorite}
            onShare={handleShare}
          />

          <div className="wl6-vd-layout">
            <div className="wl6-vd-main">
              <VehicleDetailOverview
                vehicle={vehicle}
                config={config}
                vehicleStore={vehicleStore}
                availableStoresCount={availableStores.length}
                stores={config.stores}
                formatMileage={formatMileage}
              />

              {vehicle.description && (
                <section className="wl6-vd-section">
                  <div className="wl6-vd-section__head">
                    <p className="wl6-eyebrow">Sobre o veículo</p>
                    <h2 className="wl6-vd-section__title wl6-h2">Descrição</h2>
                  </div>
                  <p className="wl6-vd-description">{vehicle.description}</p>
                  <div className="wl6-vd-desc-actions">
                    <button type="button" className="wl6-vd-btn-outline" onClick={handleShare}>
                      <Share2 className="h-4 w-4" />
                      Compartilhar
                    </button>
                  </div>
                </section>
              )}

              {vehicle.optionals?.length > 0 && (
                <VehicleDetailFeatures optionals={vehicle.optionals} />
              )}

              <VehicleDetailSpecs
                vehicle={vehicle}
                formatPrice={formatPrice}
              />

              <VehicleDetailLocation vehicleStore={vehicleStore} />
            </div>

            <VehicleDetailSidebar
              vehicle={vehicle}
              config={config}
              vehicleStore={vehicleStore}
              availableStoresCount={availableStores.length}
              userLocation={userLocation}
              onRequestLocation={handleRequestLocation}
              buttonColor={buttonColor}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              showTestDrive={!!showTestDrive}
              testDriveService={testDriveService}
              financingService={financingService}
              onWhatsApp={handleWhatsApp}
            />
          </div>

          {vehicleStore?.ad?.showRelatedOffers && relatedVehicles.length > 0 && (
            <VehicleDetailRelated vehicles={relatedVehicles} />
          )}

          {vehicleStore.ad?.warning && (
            <div className="wl6-vd-warning">
              <h3 className="wl6-vd-warning__title">Aviso</h3>
              <p className="wl6-vd-warning__text">{vehicleStore.ad.warning}</p>
            </div>
          )}
        </div>
      </section>

      <Footer config={config} />
    </Layout6Root>
  );
};

export default VehicleDetail;
